import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { ConVersion } from '../../api/cliente';
import type { Borrador, IdiomaWeb, Imagen, MapaDelSitio, Sitio, TipoImagen, TipoSeccion } from '../../api/ops/web';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Chip } from '../../componentes/Chip';
import { Deshacer } from '../../componentes/Deshacer';
import { EstadoCargando, EstadoError, EstadoVacia } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import { PanelEnlaces, PanelMarca, PanelNuevaSeccion, PanelPortada, PanelSeccion, PanelSeo } from './Paneles';
import { Publicar } from './Publicar';
import { cambioDe, chipSitio, chipsSeccion, idiomasDelSelector, NOMBRE_IDIOMA, sinProtocolo, textoPublicar, textosDe, tituloPropio, tituloSeccion } from './reglas';
import { AYUDA_TU_WEB, TEXTO_BORRADOR_CAMBIADO } from './textos';

/**
 * «Tu web» (13.1 a 13.3): una sola lista en el orden real de la página (Marca, Google y redes, Portada, cada sección y
 * Enlaces) con la dirección sin `https://` (G125) y su estado, y al lado la vista previa del borrador actual (G577). Todo
 * va al borrador (`updateSiteDraft` con `If-Match`); la web no cambia hasta publicar. Textos por idioma (G615: español e
 * inglés; «Sin traducir» lo que aún no tiene texto en ese idioma). Quitar una sección, con «Deshacer» 5 s (G159).
 *
 * El botón principal abre «Publicar» (13.3). Sin `web.sitio.publicar` no sale. La dirección se cambia en Negocio
 * (`changeSlug`, sin dominio propio).
 */
type Abierto = 'marca' | 'seo' | 'portada' | 'enlaces' | 'nueva' | 'publicar' | { seccion: string } | null;

interface Cargado {
    sitio: Sitio;
    borrador: ConVersion<Borrador>;
    tipos: TipoSeccion[];
    imagenes: Imagen[];
}

const esNoEncontrado = (e: unknown) => e instanceof ErrorApi && e.codigo === 'not_found';

export function TuWeb(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, navegar } = props;
    const { puede } = usePermisos();
    const publica = puede('web.sitio.publicar');
    const avisar = useAvisoBreve();
    const [cargado, setCargado] = useState<Cargado | null>(null);
    const [sinWeb, setSinWeb] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [idioma, setIdioma] = useState<IdiomaWeb | null>(null);
    const [abierto, setAbierto] = useState<Abierto>(null);
    const [creando, setCreando] = useState(false);
    const [mapa, setMapa] = useState<MapaDelSitio | null>(null);
    const [cambios, setCambios] = useState(0);
    const [quitada, setQuitada] = useState<string | null>(null);
    const retraso = useEnvioRetrasado();
    const vivo = useRef(true);

    useEffect(() => {
        vivo.current = true;
        return () => {
            vivo.current = false;
        };
    }, []);

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        let actual = true;
        ops.getSite(negocio)
            .then((sitio) =>
                Promise.all([ops.getSiteDraft(negocio), ops.listSectionTypes(negocio), ops.listWebMedia(negocio, { limit: 100 }).catch(() => ({ data: [] as Imagen[] }))]).then(([borrador, tipos, imagenes]) => {
                    if (actual) {
                        setCargado({ sitio, borrador, tipos: tipos.data, imagenes: imagenes.data });
                        setSinWeb(false);
                        setError(null);
                    }
                }),
            )
            .catch((e: unknown) => {
                if (!actual) {
                    return;
                }
                if (esNoEncontrado(e)) {
                    setSinWeb(true);
                    setError(null);
                } else {
                    setError(e);
                }
            });
        ops.getSitemapStatus(negocio)
            .then((m) => {
                if (actual) {
                    setMapa(m);
                }
            })
            .catch(() => undefined);
        return () => {
            actual = false;
        };
    }, [ops, negocio, intento]);

    const sitio = cargado?.sitio ?? null;
    const borrador = cargado?.borrador.datos ?? null;
    const principal = borrador?.default_locale ?? 'es-ES';
    const enIdioma = idioma ?? principal;
    const tipos = cargado?.tipos ?? [];
    const nombreSeccion = (tipo: string) => (borrador === null ? tipo : tituloSeccion(borrador, principal, tipo, tipos));

    useCabecera({
        ayuda: AYUDA_TU_WEB,
        acciones:
            sitio === null || !publica
                ? []
                : [{ clave: 'publicar', texto: textoPublicar(sitio, cambios), icono: 'rocket', desactivado: sitio.active_takedown != null, onClick: () => { setAbierto('publicar'); } }],
    });

    /** Guarda el borrador entero (`If-Match`); si otro lo cambió (412), lo vuelve a leer y lo dice. */
    const guardar = useCallback(
        async (nuevo: Borrador): Promise<boolean> => {
            if (cargado === null) {
                return false;
            }
            try {
                const r = await ops.updateSiteDraft(negocio, cambioDe(nuevo), cargado.borrador.version ?? '');
                const s = await ops.getSite(negocio).catch(() => cargado.sitio);
                if (vivo.current) {
                    setCargado((c) => (c === null ? c : { ...c, borrador: r, sitio: s }));
                }
                return true;
            } catch (e) {
                if (e instanceof ErrorApi && e.codigo === 'version_conflict') {
                    avisar({ tipo: 'error', texto: TEXTO_BORRADOR_CAMBIADO });
                    recargar();
                } else {
                    avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido guardar. Inténtalo otra vez.' });
                }
                return false;
            }
        },
        [cargado, ops, negocio, avisar, recargar],
    );

    const subir = useCallback(
        async (archivo: File, tipo: TipoImagen): Promise<Imagen | null> => {
            try {
                const imagen = await ops.uploadWebMedia(negocio, archivo, tipo);
                setCargado((c) => (c === null ? c : { ...c, imagenes: [imagen, ...c.imagenes] }));
                return imagen;
            } catch (e) {
                avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido subir la imagen.' });
                return null;
            }
        },
        [ops, negocio, avisar],
    );

    if (error !== null) {
        return <EstadoError seccion="tu web" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (sinWeb) {
        return (
            <EstadoVacia
                seccion="Tu web"
                accion={
                    soloLectura ? undefined : (
                        <Boton
                            icono="plus"
                            movil={movil}
                            ocupado={creando}
                            onClick={() => {
                                setCreando(true);
                                ops.createSiteDraft(negocio)
                                    .then(() => { recargar(); })
                                    .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido crear el borrador.' }); })
                                    .finally(() => { setCreando(false); });
                            }}
                        >
                            Crear el borrador
                        </Boton>
                    )
                }
            />
        );
    }
    if (cargado === null || sitio === null || borrador === null) {
        return <EstadoCargando seccion="tu web" />;
    }

    const chip = chipSitio(sitio);
    const textos = textosDe(borrador, enIdioma);
    const secciones = borrador.sections.filter((s) => s.section_type !== quitada);
    const imagen = (id: string | null | undefined) => cargado.imagenes.find((m) => m.id === id) ?? null;
    const traducir = enIdioma !== principal;
    const sinTraducir = <Chip estado="pending">Sin traducir</Chip>;
    const logo = imagen(borrador.brand.logo_media_id);

    const fila = (clave: string, etiqueta: string, icono: NombreIcono, titulo: ReactNode, linea: string, onAbrir: () => void, extra?: ReactNode, baldosa?: ReactNode) => (
        <div key={clave} className="rv-ca-fila-caja">
            <div className="rv-ca-fila rv-ca-fila--abre" role="button" tabIndex={0} aria-label={`Abrir «${etiqueta}»`} onClick={onAbrir} onKeyDown={(e) => { if (e.key === 'Enter') { onAbrir(); } }}>
                {baldosa ?? (
                    <span className="rv-ca-baldosa">
                        <Icono nombre={icono} />
                    </span>
                )}
                <span className="rv-ca-fila__texto">
                    <strong>{titulo}</strong>
                    {linea !== '' && <span>{linea}</span>}
                </span>
                {extra}
                <Icono nombre="chevron-right" className="rv-ca-chevron" />
            </div>
        </div>
    );

    const retirada = sitio.active_takedown ?? null;
    const avisoCierre = sitio.status === 'closed' ? (sitio.closed_reasons.includes('seasonal_pause') ? 'temporada' : 'cerrada') : null;

    return (
        <div className="rv-wb-tuweb">
            {retirada !== null && (
                <p className="rv-wb-banda rv-wb-banda--roja" role="alert">
                    <Icono nombre="ban" />
                    <span>
                        <strong>Web retirada por abuso.</strong> {`Reservaria la ha retirado por abuso. Motivo: ${retirada.reason_text}. Te lo hemos avisado también en la campana. Mientras siga retirada no se puede volver a publicar. Escríbenos si crees que es un error.`}
                    </span>
                </p>
            )}
            {avisoCierre === 'temporada' && (
                <p className="rv-wb-banda">
                    <Icono nombre="calendar-x" />
                    <span>
                        <strong>Cerrada por temporada.</strong>{' '}
                        {sitio.reopens_on == null ? 'Tus clientes ven que está cerrada y pueden cancelar lo que tengan.' : `Tus clientes ven que vuelves el ${new Date(`${sitio.reopens_on}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })} y pueden cancelar lo que tengan.`}
                    </span>
                </p>
            )}
            {avisoCierre === 'cerrada' && (
                <p className="rv-wb-banda">
                    <Icono nombre="lock" />
                    <span>
                        <strong>Web cerrada.</strong> El negocio está en solo lectura: tus clientes ven la web, pero no pueden reservar ni comprar; sí cancelar.
                    </span>
                </p>
            )}
            <div className="rv-wb-columnas">
                <section className="rv-ca-lista rv-wb-lista" aria-label="Tu web">
                    <div className="rv-wb-cab">
                        <Icono nombre="globe" />
                        <strong>{sinProtocolo(sitio.public_url)}</strong>
                        <Chip estado={chip.estado}>{chip.texto}</Chip>
                        <button type="button" className="rv-wb-enlace" onClick={() => { navegar('/negocio'); }}>
                            Cambiar
                        </button>
                    </div>
                    <div className="rv-wb-idiomas">
                        <div role="radiogroup" aria-label="Idioma de los textos" className="rv-filtros">
                            {idiomasDelSelector(principal).map((i) => (
                                <button key={i} type="button" role="radio" aria-checked={i === enIdioma} className={`rv-filtros__b${i === enIdioma ? ' rv-filtros__b--on' : ''}`} onClick={() => { setIdioma(i); }}>
                                    <span>{i === principal ? `${NOMBRE_IDIOMA[i]} · principal` : NOMBRE_IDIOMA[i]}</span>
                                </button>
                            ))}
                        </div>
                        <small>{traducir ? `Los textos van por idioma. En ${NOMBRE_IDIOMA[enIdioma].toLocaleLowerCase('es')}: ${sinProtocolo(sitio.public_url)}/en` : 'Los textos van por idioma. Lo que no traduzcas sale en el idioma principal.'}</small>
                    </div>
                    {fila(
                        'marca',
                        'Marca',
                        'palette',
                        'Marca',
                        [borrador.brand.logo_media_id == null ? null : '1 imagen', `letra ${({ system: 'del sistema', sans: 'sin serifa', serif: 'con serifa', rounded: 'redondeada' } as const)[borrador.brand.font]}`].filter((x) => x !== null).join(' · '),
                        () => { setAbierto('marca'); },
                        <span className="rv-wb-colores" aria-hidden="true">
                            <i style={{ background: borrador.brand.primary_color }} />
                            {borrador.brand.accent_color !== undefined && <i style={{ background: borrador.brand.accent_color }} />}
                        </span>,
                        logo?.url == null ? undefined : <img className="rv-ca-baldosa rv-wb-logo" src={logo.url} alt="" />,
                    )}
                    {fila('seo', 'Google y redes', 'search', <>Google y redes {traducir && textos === null && sinTraducir}</>, [textos?.seo.title ?? textosDe(borrador, principal)?.seo.title, textos?.seo.city].filter((x) => x != null && x !== '').join(' · '), () => { setAbierto('seo'); })}
                    {fila('portada', 'Portada', 'image', <>Portada {traducir && (textos?.texts?.tagline ?? '') === '' && sinTraducir}</>, textos?.texts?.tagline ?? textosDe(borrador, principal)?.texts?.tagline ?? '', () => { setAbierto('portada'); })}
                    {secciones.map((s) => {
                        const tipo = tipos.find((t) => t.code === s.section_type);
                        return fila(
                            s.section_type,
                            tituloSeccion(borrador, enIdioma, s.section_type, tipos),
                            'layout-template',
                            <>
                                {tituloSeccion(borrador, enIdioma, s.section_type, tipos)}
                                {traducir && tituloPropio(borrador, enIdioma, s.section_type) === null && sinTraducir}
                                {chipsSeccion(s, tipo).map((c) => (
                                    <Chip key={c.texto} estado={c.estado}>
                                        {c.texto}
                                    </Chip>
                                ))}
                            </>,
                            tipo?.name ?? s.module,
                            () => { setAbierto({ seccion: s.section_type }); },
                        );
                    })}
                    {fila('enlaces', 'Enlaces', 'link', 'Enlaces', [borrador.links?.instagram_handle == null ? null : `@${borrador.links.instagram_handle}`, (borrador.links?.items ?? []).length > 0 ? `${String(borrador.links?.items?.length ?? 0)} enlaces tuyos` : null].filter((x) => x !== null).join(' · '), () => { setAbierto('enlaces'); })}
                    {retraso.pendiente !== null && (
                        <div className="rv-ca-deshacer">
                            <span>{retraso.pendiente.texto}</span>
                            <Deshacer clave={retraso.pendiente.clave} onDeshacer={() => { retraso.deshacer(); }} />
                        </div>
                    )}
                    {!soloLectura && (
                        <div className="rv-wb-pie">
                            <Boton tipo="secundario" icono="plus" movil={movil} onClick={() => { setAbierto('nueva'); }}>
                                Añadir una sección
                            </Boton>
                        </div>
                    )}
                </section>
                <VistaPrevia {...props} idioma={enIdioma} revision={borrador.revision} ancla={anclaDe(abierto)} direccion={`${sinProtocolo(sitio.public_url)}${enIdioma === principal ? '' : '/en'}`} />
            </div>

            <PanelMarca abierto={abierto === 'marca'} borrador={borrador} imagenes={cargado.imagenes} soloLectura={soloLectura} movil={movil} onCerrar={() => { setAbierto(null); }} guardar={guardar} subir={subir} />
            <PanelSeo abierto={abierto === 'seo'} borrador={borrador} idioma={enIdioma} mapa={mapa} imagenes={cargado.imagenes} soloLectura={soloLectura} movil={movil} onCerrar={() => { setAbierto(null); }} guardar={guardar} subir={subir} ops={ops} negocio={negocio} puedeRegenerar={publica} onMapa={setMapa} />
            <PanelPortada abierto={abierto === 'portada'} borrador={borrador} idioma={enIdioma} tipos={tipos} imagenes={cargado.imagenes} soloLectura={soloLectura} movil={movil} onCerrar={() => { setAbierto(null); }} guardar={guardar} subir={subir} />
            <PanelEnlaces abierto={abierto === 'enlaces'} borrador={borrador} soloLectura={soloLectura} movil={movil} onCerrar={() => { setAbierto(null); }} guardar={guardar} />
            <PanelNuevaSeccion abierto={abierto === 'nueva'} borrador={borrador} tipos={tipos} movil={movil} onCerrar={() => { setAbierto(null); }} guardar={guardar} />
            {typeof abierto === 'object' && abierto !== null && (
                <PanelSeccion
                    key={abierto.seccion}
                    tipoSeccion={abierto.seccion}
                    borrador={borrador}
                    idioma={enIdioma}
                    tipos={tipos}
                    publicUrl={sitio.public_url}
                    imagenes={cargado.imagenes}
                    soloLectura={soloLectura}
                    movil={movil}
                    onCerrar={() => { setAbierto(null); }}
                    guardar={guardar}
                    subir={subir}
                    onQuitar={() => {
                        const tipo = abierto.seccion;
                        setAbierto(null);
                        setQuitada(tipo);
                        retraso.programar({
                            texto: `«${nombreSeccion(tipo)}» sale del borrador.`,
                            donde: 'lista',
                            enviar: () => {
                                void guardar({ ...borrador, sections: borrador.sections.filter((s) => s.section_type !== tipo) }).finally(() => { setQuitada(null); });
                            },
                            alDeshacer: () => { setQuitada(null); },
                        });
                    }}
                />
            )}
            {publica && (
                <Publicar
                    {...props}
                    abierto={abierto === 'publicar'}
                    onCerrar={() => { setAbierto(null); }}
                    sitio={sitio}
                    borrador={borrador}
                    nombreSeccion={nombreSeccion}
                    onCambios={setCambios}
                    onPublicada={recargar}
                />
            )}
        </div>
    );
}

/** El ancla de la web pública (13.7) de lo que se está tocando, para que la vista previa salte ahí (13.1). */
function anclaDe(abierto: Abierto): string {
    if (abierto === 'portada') {
        return '#w-portada';
    }
    if (abierto === 'enlaces') {
        return '#w-enlaces';
    }
    if (typeof abierto === 'object' && abierto !== null) {
        return `#w-${abierto.seccion.replace(/[._]/g, '-')}`;
    }
    return '';
}

const ANCHO_ORDENADOR = 1280;

/**
 * Vista previa del borrador actual (13.1, G577, G636): la web pública dibujada (13.7) dentro del editor, en un marco de
 * móvil o de ordenador, con el enlace privado de `createSitePreview` en el idioma que se edita. Enseña siempre el borrador
 * actual: tras cada guardado vuelve a cargarse, y salta a la parte que se toca (el ancla de cada sección). En ordenador la
 * web se pinta a 1280 y se escala al ancho de la columna.
 */
function VistaPrevia({ ops, negocio, movil, idioma, revision, ancla, direccion }: PropsPantalla & { idioma: IdiomaWeb; revision: number; ancla: string; direccion: string }) {
    const [dispositivo, setDispositivo] = useState<'movil' | 'ordenador'>('movil');
    const [leida, setLeida] = useState<{ idioma: IdiomaWeb; url: string | null } | null>(null);
    const [ancho, setAncho] = useState(400);
    const caja = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        let vivo = true;
        ops.createSitePreview(negocio, idioma)
            .then((v) => { if (vivo) { setLeida({ idioma, url: v.url }); } })
            .catch(() => { if (vivo) { setLeida({ idioma, url: null }); } });
        return () => { vivo = false; };
    }, [ops, negocio, idioma]);

    useEffect(() => {
        const el = caja.current;
        if (el === null || typeof ResizeObserver === 'undefined') {
            return;
        }
        const observador = new ResizeObserver(() => { setAncho(el.clientWidth); });
        observador.observe(el);
        return () => { observador.disconnect(); };
    }, []);

    // La de otro idioma no vale: mientras llega la nueva, «Preparando…».
    const actual = leida?.idioma === idioma ? leida : null;
    const url = actual?.url ?? null;
    const fallo = actual !== null && actual.url === null;
    const ordenador = dispositivo === 'ordenador';
    const escala = Math.min(1, Math.max(0.2, ancho / ANCHO_ORDENADOR));
    const alto = ordenador ? Math.round(ancho * 0.62) : (movil ? 560 : 700);
    return (
        <aside className="rv-wb-vista" aria-label="Vista previa">
            <div className="rv-wb-vista__cab">
                <strong>Vista previa</strong>
                <div role="radiogroup" aria-label="Dispositivo" className="rv-wb-dispositivo">
                    <span aria-hidden="true" className="rv-wb-dispositivo__marca" style={{ transform: `translateX(${ordenador ? '100%' : '0'})` }} />
                    {([['movil', 'Móvil', 'smartphone'], ['ordenador', 'Ordenador', 'monitor']] as const).map(([clave, texto, icono]) => (
                        <button key={clave} type="button" role="radio" aria-checked={dispositivo === clave} className={dispositivo === clave ? 'rv-wb-dispositivo__b rv-wb-dispositivo__b--on' : 'rv-wb-dispositivo__b'} onClick={() => { setDispositivo(clave); }}>
                            <Icono nombre={icono} />
                            <span>{texto}</span>
                        </button>
                    ))}
                </div>
            </div>
            <div ref={caja} className="rv-wb-vista__caja">
                <div className={ordenador ? 'rv-wb-marco rv-wb-marco--ordenador' : 'rv-wb-marco rv-wb-marco--movil'}>
                    {ordenador && (
                        <div className="rv-wb-marco__barra">
                            <span aria-hidden="true" className="rv-wb-marco__puntos"><i /><i /><i /></span>
                            <span className="rv-wb-marco__direccion"><Icono nombre="lock" />{direccion}</span>
                        </div>
                    )}
                    <div className="rv-wb-marco__pantalla" style={{ height: alto }}>
                        {url !== null && (
                            <iframe
                                key={`${url}|${String(revision)}`}
                                title="Vista previa de tu web"
                                src={`${url}${ancla}`}
                                className="rv-wb-marco__web"
                                style={ordenador ? { width: ANCHO_ORDENADOR, height: Math.round(alto / escala), transform: `scale(${String(escala)})` } : undefined}
                            />
                        )}
                        {url === null && !fallo && <p className="rv-wb-marco__nota">Preparando la vista previa…</p>}
                        {fallo && <p className="rv-wb-marco__nota">No se ha podido preparar la vista previa. Vuelve a intentarlo en un momento.</p>}
                    </div>
                </div>
            </div>
        </aside>
    );
}
